import React, { useState, useEffect } from 'react';
import { Shield, Settings, Check, X, Lock } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../config/axios';
import TwoFactorSetup from '../Auth/TwoFactorSetup';
import LoadingSpinner from '../UI/LoadingSpinner';

const SecuritySettings = () => {
    const [twoFactorStatus, setTwoFactorStatus] = useState({
        twoFactorEnabled: false,
        twoFactorAlwaysRequired: false,
        trustedDevicesCount: 0
    });
    const [loading, setLoading] = useState(true);
    const [showSetupModal, setShowSetupModal] = useState(false);
    const [showDisableConfirm, setShowDisableConfirm] = useState(false);
    const [disablePassword, setDisablePassword] = useState('');
    const [disabling, setDisabling] = useState(false);

    useEffect(() => {
        fetchStatus();
    }, []);

    const fetchStatus = async () => {
        try {
            const response = await api.get('/api/auth/2fa/status');
            setTwoFactorStatus(response.data);
        } catch (error) {
            toast.error('Failed to fetch 2FA status');
        } finally {
            setLoading(false);
        }
    };

    const handleToggleAlwaysRequired = async () => {
        try {
            const response = await api.post('/api/auth/2fa/toggle-always-required');
            setTwoFactorStatus(prev => ({
                ...prev,
                twoFactorAlwaysRequired: response.data.twoFactorAlwaysRequired
            }));
            toast.success(response.data.message);
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed to update setting');
        }
    };

    const handleDisable2FA = async (e) => {
        e.preventDefault();

        if (!disablePassword) {
            toast.error('Please enter your password');
            return;
        }

        setDisabling(true);
        try {
            await api.post('/api/auth/2fa/disable', { password: disablePassword });
            setTwoFactorStatus({
                twoFactorEnabled: false,
                twoFactorAlwaysRequired: false,
                trustedDevicesCount: 0
            });
            setShowDisableConfirm(false);
            setDisablePassword('');
            toast.success('2FA disabled successfully');
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed to disable 2FA');
        } finally {
            setDisabling(false);
        }
    };

    const handleSetupComplete = () => {
        setTwoFactorStatus(prev => ({
            ...prev,
            twoFactorEnabled: true
        }));
    };

    if (loading) {
        return (
            <div className="card p-6">
                <div className="flex justify-center">
                    <LoadingSpinner size="md" />
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-8">
            <div className="card-premium p-8">
                <div className="flex items-center space-x-4 mb-10">
                    <div className="p-3 bg-brand-500/10 rounded-2xl">
                        <Shield className="h-6 w-6 text-brand-600 dark:text-brand-400" />
                    </div>
                    <div>
                        <h3 className="text-xl font-bold text-surface-900 dark:text-white">Security Infrastructure</h3>
                        <p className="text-sm font-medium text-surface-500">Manage your advanced access control and authentication protocols</p>
                    </div>
                </div>

                <div className="space-y-10">
                    {/* 2FA Status Section */}
                    <div className="flex flex-col md:flex-row md:items-center justify-between p-6 rounded-3xl bg-surface-50 dark:bg-surface-800/40 border border-surface-100 dark:border-surface-700/50 gap-6">
                        <div className="flex-1 space-y-2">
                            <div className="flex items-center gap-3">
                                <h4 className="text-lg font-bold text-surface-900 dark:text-white">Multi-Factor Authentication</h4>
                                {twoFactorStatus.twoFactorEnabled ? (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-success/10 text-success border border-success/20 uppercase tracking-widest">
                                        <Check className="h-2.5 w-2.5 mr-1" />
                                        Protected
                                    </span>
                                ) : (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-danger/10 text-danger border border-danger/20 uppercase tracking-widest">
                                        <X className="h-2.5 w-2.5 mr-1" />
                                        Unprotected
                                    </span>
                                )}
                            </div>
                            <p className="text-sm text-surface-500 font-medium leading-relaxed max-w-2xl">
                                {twoFactorStatus.twoFactorEnabled
                                    ? 'Your infrastructure is shielded with 2FA. Every sensitive access point requires an encrypted verification code from your trusted device.'
                                    : 'A second layer of security is highly recommended for sensitive production environments. Enable 2FA to prevent unauthorized access.'
                                }
                            </p>
                        </div>
                        <div className="flex-shrink-0">
                            {twoFactorStatus.twoFactorEnabled ? (
                                <button
                                    onClick={() => setShowDisableConfirm(true)}
                                    className="btn-secondary !px-6 !py-3 font-bold"
                                >
                                    Deactivate 2FA
                                </button>
                            ) : (
                                <button
                                    onClick={() => setShowSetupModal(true)}
                                    className="btn-primary !px-6 !py-3 shadow-glow-brand font-bold"
                                >
                                    Activate Shield
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Advanced Controls */}
                    {twoFactorStatus.twoFactorEnabled && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            {/* Always Require 2FA Toggle Card */}
                            <div className="p-6 rounded-3xl border border-surface-200 dark:border-surface-800 space-y-4">
                                <div className="flex items-center justify-between">
                                    <h4 className="font-bold text-surface-900 dark:text-white">Enforce Every Login</h4>
                                    <button
                                        onClick={handleToggleAlwaysRequired}
                                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ring-offset-bg bg-surface-200 dark:bg-surface-700 ${twoFactorStatus.twoFactorAlwaysRequired ? '!bg-brand-600' : ''}`}
                                    >
                                        <span
                                            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-premium-sm transition duration-200 ease-in-out ${twoFactorStatus.twoFactorAlwaysRequired ? 'translate-x-5' : 'translate-x-0'
                                                }`}
                                        />
                                    </button>
                                </div>
                                <p className="text-sm text-surface-500 font-medium leading-relaxed">
                                    Bypass device trust and require a verification code on every session establishment for maximum security.
                                </p>
                            </div>

                            {/* Trusted Devices Card */}
                            <div className="p-6 rounded-3xl border border-surface-200 dark:border-surface-800 space-y-4">
                                <div className="flex items-center space-x-3">
                                    <h4 className="font-bold text-surface-900 dark:text-white">Trusted Environments</h4>
                                    <span className="px-2 py-0.5 rounded-lg bg-brand-500/10 text-brand-600 dark:text-brand-400 text-xs font-bold font-mono">
                                        {twoFactorStatus.trustedDevicesCount}
                                    </span>
                                </div>
                                <p className="text-sm text-surface-500 font-medium leading-relaxed">
                                    Active signatures for devices that have cleared the verification process. These environments bypass 2FA checks during normal activity.
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Info Alert */}
                    <div className="flex p-6 bg-info/5 border border-info/20 rounded-[2rem]">
                        <Shield className="h-5 w-5 text-info flex-shrink-0" />
                        <div className="ml-4 space-y-1">
                            <h5 className="text-sm font-bold text-info uppercase tracking-widest">
                                Intelligent Protection Active
                            </h5>
                            <p className="text-xs text-info/80 font-medium leading-relaxed">
                                Our security engine automatically monitors for anomalies like new geolocations, unrecognized agents, or high-risk activity patterns, and will trigger a verification prompt even if strict enforcement is disabled.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Disable 2FA Confirmation Modal */}
            {showDisableConfirm && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 backdrop-blur-sm animate-fade-in">
                    <div className="card-premium max-w-md w-full p-8 space-y-8 animate-scale-in">
                        <div className="text-center space-y-4">
                            <div className="p-4 bg-danger/10 rounded-3xl inline-block">
                                <Lock className="h-8 w-8 text-danger" />
                            </div>
                            <h3 className="text-2xl font-bold text-surface-900 dark:text-white">
                                Deactivate Security Shield?
                            </h3>
                            <p className="text-sm text-surface-500 font-medium leading-relaxed">
                                This will significantly reduce your account protection. Please enter your root password to confirm this sensitive operation.
                            </p>
                        </div>

                        <form onSubmit={handleDisable2FA} className="space-y-6">
                            <div className="space-y-2">
                                <label htmlFor="disable-password" className="block text-xs font-bold text-surface-500 uppercase tracking-widest">
                                    Current Password
                                </label>
                                <input
                                    id="disable-password"
                                    type="password"
                                    value={disablePassword}
                                    onChange={(e) => setDisablePassword(e.target.value)}
                                    className="input-premium"
                                    placeholder="••••••••"
                                    required
                                    disabled={disabling}
                                />
                            </div>

                            <div className="flex flex-col sm:flex-row gap-4">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowDisableConfirm(false);
                                        setDisablePassword('');
                                    }}
                                    className="flex-1 btn-secondary !py-4 font-bold"
                                    disabled={disabling}
                                >
                                    Keep Protected
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 bg-danger hover:bg-danger-600 text-white !py-4 rounded-2xl transition-all font-bold flex items-center justify-center shadow-lg shadow-danger/20"
                                    disabled={disabling}
                                >
                                    {disabling ? <LoadingSpinner size="sm" /> : 'Deactivate Now'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* 2FA Setup Modal */}
            <TwoFactorSetup
                isOpen={showSetupModal}
                onClose={() => setShowSetupModal(false)}
                onSetupComplete={handleSetupComplete}
            />
        </div>
    );
};

export default SecuritySettings;
